import { useState, useEffect, useMemo } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useContactList } from './useContactList';
import { useUserDirectorySearch } from './useUserDirectorySearch';
import { generateInitialsAvatar } from '../utils/avatar';
import { isServerNoticesUser } from '../utils/serverNoticesUtils';

/**
 * Hook for managing a unified selectable user list for room creation/editing.
 * Combines known contacts with user directory search results.
 * Users are selectable without requiring DM rooms to exist.
 * Filters out server notices users to prevent attempting to create rooms with them.
 *
 * @returns {{
 *   users: Array<{id: string, name: string, avatar: string, isKnownContact: boolean}>,
 *   searchTerm: string,
 *   setSearchTerm: (term: string) => void,
 *   isSearching: boolean,
 *   error: string | null
 * }}
 */
export const useSelectableUserList = () => {
  const client = useMatrixClient();
  const [searchTerm, setSearchTerm] = useState('');
  const { contactList } = useContactList();
  const { searchUsers, results: searchResults, isSearching, error } = useUserDirectorySearch();

  const myUserId = client?.getUserId();

  // Debounced user directory search
  useEffect(() => {
    const trimmedSearch = searchTerm.trim();

    // Only search if term is at least 2 characters
    if (trimmedSearch.length >= 2) {
      const timer = setTimeout(() => {
        searchUsers(trimmedSearch, 20);
      }, 300); // 300ms debounce

      return () => clearTimeout(timer);
    }
  }, [searchTerm, searchUsers]);

  /**
   * Convert contact list to standardized user format
   * contactList items have: {id, name, avatar: string}
   * Filters out current user and server notices users
   */
  const knownUsers = useMemo(() => {
    return contactList
      .filter(contact => contact.id !== myUserId && !isServerNoticesUser(contact.id))
      .map(contact => ({
        id: contact.id,
        name: contact.name,
        avatar: contact.avatar || generateInitialsAvatar(contact.name), // avatar is already a string URL or initials
        organization: contact.organization || '',
        lastActivityTimestamp: contact.lastActivityTimestamp || null,
        presence: contact.presence || null,
        online: contact.online || false,
        powerLevel: contact.powerLevel || 0,
        isKnownContact: true,
      }));
  }, [contactList, myUserId]);

  /**
   * Convert search results to standardized user format
   * Search results have: {user_id, display_name, avatar_url}
   * Filters out server notices users
   */
  const searchResultUsers = useMemo(() => {
    return searchResults
      .filter(user => !isServerNoticesUser(user.user_id))
      .map(user => {
        // For search results in the list, use initials avatars to avoid CORS/404 issues
        // The real avatar will load when the user clicks and opens the ContactView
        // (which uses AvatarContext to properly fetch and cache the avatar)
        const avatarUrl = generateInitialsAvatar(user.display_name || user.user_id);

        return {
          id: user.user_id,
          name: user.display_name || user.user_id,
          avatar: avatarUrl,
          organization: '',
          lastActivityTimestamp: null,
          presence: null,
          online: false,
          powerLevel: 0,
          isKnownContact: false, // Mark as search result
        };
      });
  }, [searchResults]);

  /**
   * Merge known users and search results, removing duplicates.
   * Enriches known contacts with display names from search results when
   * the known contact's name has fallen back to their Matrix ID (stale SDK data).
   */
  const users = useMemo(() => {
    if (!searchTerm.trim()) {
      // No search: return only known contacts
      return knownUsers;
    }

    // Build a lookup of search results by ID for enrichment
    const searchResultMap = new Map(searchResultUsers.map(u => [u.id, u]));

    // Enrich known contacts whose name is just their Matrix ID with
    // the fresher display name from the search API (if available).
    // Only enrich name, not avatar — known contacts have real avatars
    // while search results only have initials.
    const enrichedKnownUsers = knownUsers.map(user => {
      const searchResult = searchResultMap.get(user.id);
      if (searchResult && user.name === user.id && searchResult.name !== searchResult.id) {
        return { ...user, name: searchResult.name };
      }
      return user;
    });

    // Deduplicate: prioritize known contacts over search results
    const knownUserIds = new Set(enrichedKnownUsers.map(u => u.id));
    const uniqueSearchResults = searchResultUsers.filter(u => !knownUserIds.has(u.id));

    // Combine and filter based on search term
    const combined = [...enrichedKnownUsers, ...uniqueSearchResults];

    const lowerSearch = searchTerm.toLowerCase();
    return combined.filter(user => {
      const matchesName = user.name.toLowerCase().includes(lowerSearch);
      const matchesId = user.id.toLowerCase().includes(lowerSearch);
      return matchesName || matchesId;
    });
  }, [knownUsers, searchResultUsers, searchTerm]);

  return {
    users,
    searchTerm,
    setSearchTerm,
    isSearching,
    error,
  };
};

export default useSelectableUserList;
